/**
 * Security Utilities
 * CSRF token handling, XSS protection, and input sanitization
 */

import { FormatUtils } from '../utils/format_utils.js';

export class Security {
  /**
   * Get CSRF token from meta tag or cookie
   * @returns {string|null}
   */
  static getCSRFToken() {
    // Try meta tag first
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }

    // Try cookie
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token' || name === '_token') {
        return decodeURIComponent(value);
      }
    }

    return null;
  }

  /**
   * Sanitize string input
   * @param {string} input - Input to sanitize
   * @returns {string}
   */
  static sanitizeString(input) {
    if (typeof input !== 'string') {
      return input;
    }

    // Remove script tags and event handlers
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/on\w+='[^']*'/gi, '')
      .replace(/javascript:/gi, '')
      .trim();
  }

  /**
   * Sanitize HTML (escape special characters)
   * @param {string} html - HTML to sanitize
   * @returns {string}
   */
  static sanitizeHTML(html) {
    if (typeof html !== 'string') {
      return html;
    }

    return FormatUtils.escapeHtml(html);
  }

  /**
   * Sanitize object recursively
   * @param {*} obj - Object to sanitize
   * @returns {*}
   */
  static sanitizeObject(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj === 'string') {
      return this.sanitizeString(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item));
    }

    if (typeof obj === 'object') {
      const sanitized = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          sanitized[key] = this.sanitizeObject(obj[key]);
        }
      }
      return sanitized;
    }

    return obj;
  }

  /**
   * Validate UUID v4 format
   * @param {string} uuid - UUID to validate
   * @returns {boolean}
   */
  static isValidUUID(uuid) {
    if (typeof uuid !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
  }

  /**
   * Escape ID selector for use in querySelector
   * @param {string} id - ID to escape
   * @returns {string}
   */
  static escapeSelector(id) {
    if (typeof id !== 'string') return id;
    return id.replace(/([!"#$%&'()*+,-./:;<=>?@[\]^`{|}~])/g, '\\$1');
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean}
   */
  static isValidEmail(email) {
    if (typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate URL format
   * @param {string} url - URL to validate
   * @returns {boolean}
   */
  static isValidURL(url) {
    if (typeof url !== 'string') return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Prevent XSS in innerHTML assignments
   * @param {string} html - HTML content
   * @returns {string}
   */
  static safeHTML(html) {
    if (typeof html !== 'string') return '';
    
    // Only allow safe HTML tags and attributes
    const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'span', 'div'];
    const allowedAttrs = ['href', 'target', 'rel', 'class', 'id'];
    
    // For now, escape everything - can be enhanced with DOMPurify if needed
    return FormatUtils.escapeHtml(html);
  }

  /**
   * Get secure headers for API requests
   * @returns {Object}
   */
  static getSecureHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
      headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    return headers;
  }

  /**
   * Validate form ID from URL or data attribute
   * @param {string} formId - Form ID to validate
   * @returns {boolean}
   */
  static validateFormId(formId) {
    return this.isValidUUID(formId);
  }

  /**
   * Validate entry ID from URL parameter
   * @param {string} entryId - Entry ID to validate
   * @returns {boolean}
   */
  static validateEntryId(entryId) {
    return this.isValidUUID(entryId);
  }

  /**
   * Get entry ID from URL parameters
   * @returns {string|false}
   */
  static getEntryIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const entryId = urlParams.get('entry');
    
    if (entryId && this.validateEntryId(entryId)) {
      return entryId;
    }
    
    return false;
  }
}

